import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AI_GENERATED_ICON, ICON } from '@core/util/icon';
import {
	Jurisdiction,
	VacantTaxApplicableJurisdictions,
} from '@models/jurisdictions';
import { MatterAdjustmentVacantTaxCalculated } from '@models/matter';
import { Store } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import { MatterDispatch } from '@state/matter';
import { JurisdictionsDispatch } from '@state/matter/jurisdictions/jurisdictions.dispatch';
import { map, take } from 'rxjs/operators';
import { DeleteJurisdictionConfirmationDialogComponent } from '../../property/details/dialogs/delete-jurisdiction-confirmation-dialog/delete-jurisdiction-confirmation-dialog.component';
import { TestHooks } from './jurisdiction-field.hooks';
import { VacantPropertyDialogComponent } from '../../adjustments/vacant-property-dialog/vacant-property-dialog.component';
import { AdjustmentData } from '../../adjustments/adjustments.types';
import { BaseComponent } from '@core/base.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { JurisdictionFieldSelector } from './jurisdiction-field.selector';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';

interface VM {
	matterId: string;
	showMlttToast: boolean;
	isLROToronto: boolean;
	primaryJurisdiction: Jurisdiction;
	hasVacantTaxJurisdiction: boolean;
	vacantTaxAdjustment: MatterAdjustmentVacantTaxCalculated;
	vacantTaxApplicableJurisdiction: Jurisdiction;
}

const defaultVM: VM = {
	matterId: '',
	showMlttToast: false,
	isLROToronto: false,
	primaryJurisdiction: null,
	hasVacantTaxJurisdiction: null,
	vacantTaxAdjustment: null,
	vacantTaxApplicableJurisdiction: null,
};

@UntilDestroy()
@Component({
	selector: 'app-jurisdiction-field',
	templateUrl: './jurisdiction-field.component.html',
	standalone: false,
})
export class JurisdictionFieldComponent
	extends BaseComponent<VM>
	implements OnInit
{
	testHooks = TestHooks;
	public jurisdictionFieldState$ = this.store.select(
		JurisdictionFieldSelector.GetJurisdictionFieldState,
	);

	public showAiIcon$ = this.store
		.select(MatterJurisdictionSelectors.getPrimaryFieldMappings)
		.pipe(map((mappings) => mappings?.primary));
	public ICON = ICON;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;

	constructor(
		private matterDispatch: MatterDispatch,
		private jurisdictionsDispatch: JurisdictionsDispatch,
		private matDialog: MatDialog,
		private nav: NavigationService,
		private store: Store,
	) {
		super(defaultVM);
	}

	ngOnInit(): void {
		this.jurisdictionFieldState$
			.pipe(untilDestroyed(this))
			.subscribe((state) => {
				this.VM = {
					matterId: state.matterId,
					showMlttToast: state.showMlttToast,
					isLROToronto: state.isLROToronto,
					primaryJurisdiction: state.primaryJurisdiction,
					hasVacantTaxJurisdiction: state.hasVacantTaxJurisdiction,
					vacantTaxAdjustment: state.vacantTaxAdjustment,
					vacantTaxApplicableJurisdiction:
						state.vacantTaxApplicableJurisdiction,
				};
			});
	}

	setJurisdiction(value: string) {
		this.matterDispatch.SetJurisdiction(this.VM.matterId, value);
	}

	onDeleteJurisdiction() {
		this.matDialog
			.open(DeleteJurisdictionConfirmationDialogComponent, { width: '500px' })
			.afterClosed()
			.pipe(take(1))
			.subscribe((shouldDelete: boolean) => {
				if (shouldDelete) {
					this.jurisdictionsDispatch.Delete(this.VM.primaryJurisdiction.id);
				}
			});
	}

	toggleVacantTaxCheckbox(value: boolean) {
		this.jurisdictionsDispatch.OverwriteFields(
			this.VM.primaryJurisdiction.id,
			'applyVacantTax',
			value,
		);
	}

	onSelectJurisdiction(jurisdiction: Jurisdiction) {
		if (VacantTaxApplicableJurisdictions.includes(jurisdiction.id)) {
			jurisdiction.applyVacantTax = true;
		}

		this.jurisdictionsDispatch.Create({ ...jurisdiction, primary: true });
	}

	dismissMlttToast() {
		this.matterDispatch.SetShowMlttToast(this.VM.matterId, false);
	}

	goToLTT() {
		this.nav.goToMatter_Ltt(this.VM.matterId, {});
	}

	goToVacantTaxAdjustment() {
		this.matDialog.open(VacantPropertyDialogComponent, {
			autoFocus: true,
			data: {
				adjustmentId: this.VM.vacantTaxAdjustment.adjustment.id,
			} as AdjustmentData,
			maxHeight: '100%',
			maxWidth: '900px',
			width: '90%',
		});
	}
}
